import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatPaginatorModule } from "@angular/material/paginator";
import {
  MatSidenavContainer,
  MatSidenavModule,
} from "@angular/material/sidenav";
import { MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatTab, MatTabGroup, MatTabsModule } from "@angular/material/tabs";
import { IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { LoadsService } from "./loads.service";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { combineLatest, debounceTime, distinctUntilChanged, forkJoin, Subject, Subscription, takeUntil, tap } from "rxjs";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { CompanyService } from "app/modules/components/configuration/company/company.service.service";
import { CompanyAttributes, DocumentClassAttributes, DocumentTypeAttributes, OrderAttributes, SubRouteAttributes } from "@albatrosdeveloper/ave-models-package";
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { TripLoadData } from './interfaces/trip-load-response.interface';
import { OfficeGroupService } from "app/modules/components/configuration/office-group/office-group.service";
import OfficeGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/office-group/office-group.entity";
import { SalesOfficeService } from "app/modules/components/configuration/sales-office/sales-office.service";
import SalesOfficeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity";
import { RouteGroupService } from "app/modules/components/routing/route-group/route-group.service";
import RouteGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/routing/route-group/route-group.entity";
import { SubRouteService } from "app/modules/components/routing/sub-routes/sub-route.service";
import { OrderTableComponent } from "./components/order-table/order-table.component";
import { ButtonDatepickerComponent } from "../../../../shared/button-datepicker/button-datepicker.component";
import { TripTableComponent } from "./components/trip-table/trip-table.component";
import { OrderDatepickerComponent } from "./components/order-datepicker/order-datepicker.component";
import { OrderFutureDate } from "./interfaces/order-future.interface";
import { RouteService } from "app/modules/components/routing/route/route.service";
import RouteAttributes from "@albatrosdeveloper/ave-models-package/src/schemas/routing/route/route.entity";
import { getCode } from "app/utils/method";
import RouteAreaAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/routing/route-area/route-area.entity";
import { RouteAreaService } from "app/modules/components/routing/route-area/route-area.service";
import BusinessPartnerAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity";
import { BusinessPartnerService } from "app/modules/components/business-partners/business-partner/business-partner.service";
import { getStartAndEndOfDayUTC } from "app/utils/date-utc";
import { OrdersListService } from 'app/modules/components/sales/order-list-components/order-list.service';
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { ConfigurationFiltersComponent } from "app/modules/components/filters/configuration-filters/configuration-filters.component";
import { DocumentClassService } from "app/modules/components/document/document-class/document-class.service";
import { DocumentsFiltersComponent } from "app/modules/components/filters/documents-filters/documents-filters.component";
import { ActivatedRoute } from "@angular/router";
import { RoutesFiltersComponent } from "app/modules/components/filters/routes-filters/routes-filters.component";
import { BusinessPartnersFiltersComponent } from "app/modules/components/filters/business-partners-filters/business-partners-filters.component";
import { SalesDistributionService } from "../../sales-distribution/sales-distribution.service";
import { MapModalInputData } from "app/shared/components/map-modal/models/map-modal.interface";
import { MapFilterTypeEnum, MapModalTypeEnum, MonitorDocumentTypeEnum } from "app/shared/components/map-modal/models/map-modal.enum";
import { MapModalComponent } from "app/shared/components/map-modal/map-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";

export enum LoadComponentTabIds {
  UNATTENDED_ORDERS = 0,
  ORDERS_WITHOUT_DELIVERY_ROUTE = 7,
  ORDERS_WITHOUT_DELIVERY_DATE = 6, //no mostrar en tab por eso el numero
  ORDERS_WITHOUT_LOAD_DATE = 8,
  ORDERS_WITHOUT_TRIP = 1,
  ORDERS_WITH_ROUTE_AND_TRIP = 2,
}

export interface Pagination {
  pageIndex: number;
  pageSize: number;
  totalItems: number;
}

export const LOAD_COMPONENT_QUERY_PARAMS = {
  forceInitialTabId: {
    queryParam: "forceInitialTabId",
    valueType: "number",
    description: "Forzar el tab inicial de la pantalla"
  }
}

@Component({
  selector: "app-loads",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatTabGroup,
    MatTab,
    MatMenuModule,
    MatBadgeModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatOptionModule,
    NgxMatSelectSearchModule,
    OrderTableComponent,
    ButtonDatepickerComponent,
    TripTableComponent,
    OrderDatepickerComponent,
    FilterComponent,
    FilterSectionComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    HoverMenuDirective,

  ],
  templateUrl: "./loads.component.html",
  styleUrl: "./loads.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadsComponent implements OnInit, OnDestroy {
  getCode = getCode

  companies = signal<CompanyAttributes[]>([]);
  salesOfficeGroup = signal<OfficeGroupAttributes[]>([]);
  routes: RouteAttributes[] = [];
  listFilterRoutes = signal<RouteAttributes[]>([]);
  searchRoute = ''
  areaRoutes: RouteAreaAttributes[] = [];
  listFilterAreaRoutes = signal<RouteAreaAttributes[]>([]);
  searchAreaRoute = ''
  routeGroup = signal<RouteGroupAttributes[]>([]);
  businessPartnerList: BusinessPartnerAttributes[] = [];
  searchBusiness = ''
  private searchSubjectBusiness = new Subject<string>();
  documentTypeList: DocumentTypeAttributes[] = [];
  companyFilterUseMongoId = true;

  loadCalculationMode: string | undefined;
  UMPActive: boolean

  // Para usar en la plantilla
  TabsIds = LoadComponentTabIds;
  selectedTabIndex = signal<number>(0);
  isLoading: boolean = false;
  filterForm: FormGroup;
  private dateSelection$ = new Subject<any>();
  private _onDestroy = new Subject<void>();

  tabCounts = {
    unattendedOrders: 4,
    ordersWithoutDeliveryRoute: 7,
    ordersWithoutDeliveryDate: 2,
    ordersWithoutTrip: 5,
    ordersWithRouteAndTrip: 10,
  };

  groupTabConfig = {
    tabs: [
      {
        id: LoadComponentTabIds.UNATTENDED_ORDERS,
        label: "Pedidos sin atención pasada",
        count: signal<number>(0),
        icon: "assignment_late",
        color: "#ff0000",
        content: "Pedidos sin atención",
        map: true,
        data:signal<any[]>([]),
        total: signal<number>(0),
        pagination: signal<Pagination>({
          pageIndex: 0,
          pageSize: 50,
          totalItems: 100,
        }),
        search: signal<string>(''),
        ordering: signal<{
          active: keyof OrderAttributes;
          direction: 'ASC' | 'DESC';
        }>({
          active: "creationDateTime",
          direction: "ASC"
        }),
        displayedColumns: signal<string[]>(['det', 'select', 'serial','trip', 'subRoute', 'soldTo', 'shipTo', 'address', 'deliveryDateTime', 'weight', 'actions'])
      },
      // {
      //   id: LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE,
      //   label: "Pedidos sin ruta de entrega",
      //   count: signal<number>(0),
      //   icon: "directions",
      //   color: "#ecc73a",
      //   content: "Pedidos sin ruta de entrega",
      //   data:signal<any[]>([]),
      //   total: signal<number>(0),
      //   pagination: signal<Pagination>({
      //     pageIndex: 0,
      //     pageSize: 50,
      //     totalItems: 100,
      //   }),
      //   search: signal<string>(''),
      //   ordering: signal<{
      //     active: string;
      //     direction: 'ASC' | 'DESC';
      //   }>({
      //     active: "creationDateTime",
      //     direction: "ASC"
      //   }),
      //   displayedColumns: signal<string[]>(['det', 'select', 'serial', 'soldTo', 'shipTo', 'address', 'deliveryDateTime', 'weight', 'actions'])
      // },
      // {
      //   id: LoadComponentTabIds.ORDERS_WITHOUT_LOAD_DATE,
      //   label: "Pedidos sin fecha de carga",
      //   count: signal<number>(0),
      //   icon: "schedule",
      //   color: "#ff9800",
      //   content: "Pedidos sin fecha de carga asignada",
      //   data:signal<any[]>([]),
      //   total: signal<number>(0),
      //   pagination: signal<Pagination>({
      //     pageIndex: 0,
      //     pageSize: 50,
      //     totalItems: 100,
      //   }),
      //   search: signal<string>(''),
      //   ordering: signal<{
      //     active: string;
      //     direction: 'ASC' | 'DESC';
      //   }>({
      //     active: "creationDateTime",
      //     direction: "ASC"
      //   }),
      //   displayedColumns: signal<string[]>(['det', 'select', 'serial', 'subRoute', 'soldTo', 'shipTo', 'address', 'deliveryDateTime', 'weight', 'actions'])
      // },
      /* {
        id: TabIds.ORDERS_WITHOUT_DELIVERY_DATE,
        label: "Pedidos sin fecha de entrega",
        count: signal<number>(0),
        icon: "event",
        color: "#008DB8",
        content: "Pedidos sin fecha de entrega",
        data:signal<any[]>([]),
        total: signal<number>(0),
        pagination: signal<Pagination>({
          pageIndex: 0,
          pageSize: 50,
          totalItems: 100,
        }),
        search: signal<string>(''),
        ordering: signal<{
          active: string;
          direction: 'ASC' | 'DESC';
        }>({
          active: "creationDateTime",
          direction: "ASC"
        }),
        displayedColumns: signal<string[]>(['det', 'select', 'serial', 'subRoute', 'soldTo', 'shipTo', 'address', 'weight', 'actions'])
      }, */
      {
        id: LoadComponentTabIds.ORDERS_WITHOUT_TRIP,
        label: "Pedidos sin viaje",
        count: signal<number>(0),
        icon: "directions_bus",
        color: "#ecc73a",
        content: "Pedidos sin viaje",
        map: true,
        data:signal<any[]>([]),
        total: signal<number>(0),
        pagination: signal<Pagination>({
          pageIndex: 0,
          pageSize: 50,
          totalItems: 100,
        }),
        search: signal<string>(''),
        ordering: signal<{
          active: string;
          direction: 'ASC' | 'DESC';
        }>({
          active: "creationDateTime",
          direction: "ASC"
        }),
        displayedColumns: signal<string[]>(['det', 'select', 'serial', 'subRoute','soldTo', 'shipTo', 'address', 'deliveryDateTime', 'weight', 'actions'])
      },
      {
        id: LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP,
        label: "Pedidos con ruta y viaje",
        count: signal<number>(0),
        icon: "directions_bus",
        color: "#098532",
        content: "Pedidos con ruta y viaje",
        map: false,
        data: signal<TripLoadData[]>([]),
        total: signal<number>(0),
        pagination: signal<Pagination>({
          pageIndex: 0,
          pageSize: 50,
          totalItems: 100,
        }),
        search: signal<string>(''),
        ordering: signal<{
          active: string;
          direction: 'ASC' | 'DESC';
        }>({
          active: "creationDateTime",
          direction: "ASC"
        }),
        displayedColumns: signal<string[]>(['det', 'select', 'serial', 'soldTo', 'shipTo', 'address', 'deliveryDateTime', 'weight', 'actions'])
      },
    ],
    documentClasses: signal<DocumentClassAttributes[]>([]),
  };

  dataSource = new MatTableDataSource<any[]>([]);

  // Configuración de los selects múltiples
  multiSelectConfig = {
    salesOffice: {
      formControlName: "salesOffice",
      searchCtrl: new FormControl(),
      filteredData: signal<any[]>([]),
      isIndeterminate: false,
      isAllSelected: false,
      options: signal<SalesOfficeAttributes[]>([]),
    },
    subRoute: {
      formControlName: "subRoute",
      searchCtrl: new FormControl(),
      filteredData: signal<any[]>([]),
      isIndeterminate: false,
      isAllSelected: false,
      options: signal<SubRouteAttributes[]>([]),
    },
  };

  selectedDate = new Date()
  selectedOrdersFuture = signal<OrderFutureDate>({ days: [], total: 0 })
  selectedOrdersPast = signal<OrderFutureDate>({ days: [], total: 0 })
  isLoadingOrdersFuture = signal(false)
  isLoadingOrdersPast = signal(false)
  private lastLoadedFutureDate: string | null = null
  private lastLoadedPastDate: string | null = null
  
  //BUSCADOR
  searchSubscription: Subscription;
  searchHandle = ''

  onInitFinished: boolean = false;

  constructor(
    private readonly companyService: CompanyService,
    private readonly officegroupService: OfficeGroupService,
    private readonly salesOfficeService: SalesOfficeService,
    private readonly routeService: RouteService,
    private readonly routeAreaService: RouteAreaService,
    private readonly routeGroupService: RouteGroupService,
    private readonly subrouteService: SubRouteService,
    private readonly businessPartnerService: BusinessPartnerService,
    private readonly LoadsService: LoadsService,
    private ordersListService: OrdersListService,
    private readonly searchService: SearchService,
    private readonly documentClassService: DocumentClassService,
    private readonly salesDistributionService: SalesDistributionService,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
    this.searchSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.onHandleSearch();
  }

  public onHandleSearch(){
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.searchHandle = term;
        this.resetPagintaionDefaultValues();
        this.fetchAllData();
      });
  }

  ngOnInit(): void {

    if(!this.companyFilterUseMongoId) {
      throw new Error('Company is intended to be used with mongoIds');
    }

    this.subscribeToActivatedRoute();

    this.filterForm = this.fb.group({
      uploadDate: [new Date()],
      companyId: [null],
      salesOfficeGroupId: [],
      salesOfficeId: [],
      routeId: [],
      routeAreaId: [],
      routeGroupId: [],
      subRouteId: [],
      customerId: [],
      documentClassId: [],
    });

    this.initFiltersOptions();
    this.initSearchSelectsListenersAndData();
    // this.initDependentSelectsListeners();
    this.fetchDocumentClass();
    this.fetchTypeDocuments();

    this.dateSelection$.pipe(
      debounceTime(500),
      takeUntil(this._onDestroy)
    ).subscribe(event => {
      this.filterForm.get("uploadDate").setValue(event);
      this.fetchAllData();
    });

    this.onInitFinished = true;
  }

  private fetchTypeDocuments(): void {
    this.documentClassService.findToComboBox(null, "DT002").subscribe({
      next: (res: IRequestResult<DocumentTypeAttributes[]>) => {
        this.documentTypeList = res?.data ?? [];
      },
      error: (err) => {
        this.snackBar.open(err.error.message || "Erro al obtener tipo de documentos.","Cerrar",{ duration: 5000});
      },
    });
  }

  private fetchDocumentClass(): void {
    /* Se filtran las clases de documentos de tipo pedido, 
      ya que los documentos de carga tienen las mismas clases */
    let dataToFilter = {
      type: 'DT002',
    }
    this.ordersListService.fetchDocumentClasses(dataToFilter)
      .subscribe((res) => {
        this.groupTabConfig.documentClasses
          .update(() => res.data);
      });
  }

  onDateFutureSelected(event: any) {
    this.selectedDate = event;
    this.lastLoadedFutureDate = null;
    this.getOrdersFuture();
    this.onDateSelected(event)
  }


  onDatePastSelected(event: any) {
    this.selectedDate = event;
    this.lastLoadedPastDate = null;
    this.getOrdersPast();
    this.onDateSelected(event)
  } 

  onDateSelected(event: any) {
    this.dateSelection$.next(event);
  }

  private initDependentSelectsListeners() {
    this.filterForm
      .get("companyId")
      .valueChanges.pipe(takeUntil(this._onDestroy))
      .subscribe((companyId) => {
        if (companyId) {
          this.filterForm.get("salesOfficeGroupId").enable();
          this.filterForm.get("route").enable();
          this.filterForm.get("routeArea").enable();
          this.filterForm.get("routeGroup").enable();
          this.fetchSalesOfficesGroups(companyId);
          this.fetchAllRoute(companyId)
          this.fetchAllAreaRoute(companyId)
          this.fetchRouteGroups(companyId);
        } else {
          this.filterForm.get("salesOfficeGroupId").disable();
          this.filterForm.get("salesOfficeId").disable();
          this.filterForm.get("route").disable();
          this.filterForm.get("routeArea").disable();
          this.filterForm.get("routeGroup").disable();
          this.filterForm.get("subRoute").disable();
          this.filterForm.get("customerId").disable();
          this.filterForm.get("salesOfficeGroupId").setValue(null);
          this.filterForm.get("salesOfficeId").setValue(null);
          this.filterForm.get("route").setValue(null);
          this.filterForm.get("routeArea").setValue(null);
          this.filterForm.get("routeGroup").setValue(null);
          this.filterForm.get("subRoute").setValue(null);
          this.filterForm.get("customerId").setValue(null);
        }
      });

    this.filterForm
      .get("salesOfficeGroupId")
      .valueChanges.pipe(takeUntil(this._onDestroy))
      .subscribe((salesOfficeGroup) => {
        if (salesOfficeGroup) {
          this.filterForm.get("salesOfficeId").enable();
          this.fetchSalesOffices([salesOfficeGroup]);
        } else {
          this.filterForm.get("salesOfficeId").disable();
          this.filterForm.get("subRouteId").disable(); 
          this.filterForm.get("salesOfficeId").setValue(null);
          this.filterForm.get("subRouteId").setValue(null); 
        }
      });

    combineLatest([
        this.filterForm.get('routeId').valueChanges,
        this.filterForm.get('routeAreaId').valueChanges,
        this.filterForm.get('routeGroupId').valueChanges
      ])
      .pipe(takeUntil(this._onDestroy))
      .subscribe(([route, routeArea, routeGroup]) => {
        if (route || routeArea || routeGroup) {
          this.filterForm.get('subRouteId').enable();
          this.fetchSubRoutes(
            route ? [route] : [], 
            routeArea ? [routeArea]: [],
            routeGroup ? [routeGroup] : []
          );

          if(route){
            this.filterForm.get('customerId').enable();
            this.setupBusinessSearchControl();
            this.getAllBusinessPartner();
          }
        } else {
          this.filterForm.get('subRouteId').disable();
          this.filterForm.get('subRouteId').setValue(null);
          this.filterForm.get('customerId').disable();
          this.filterForm.get('customerId').setValue(null);
        }
      });
  }

  private initSearchSelectsListenersAndData() {
    Object.keys(this.multiSelectConfig).forEach((key) => {

      this.multiSelectConfig[key].searchCtrl.valueChanges
        .pipe(takeUntil(this._onDestroy))
        .subscribe(() => {
          this.filterOptions(key);
          this.updateCheckboxState(key);
        });
        
    });
  }

  private initFiltersOptions() {
    this.fetchCompanies();
  }

  filterOptions(selectKey: string) {
    const searchValue =
      this.multiSelectConfig[selectKey].searchCtrl.value?.toLowerCase() || "";
  
    this.multiSelectConfig[selectKey].filteredData.update(() => {
      const optionsValue = this.multiSelectConfig[selectKey].options(); // Asegurar que se llama al signal
  
      return searchValue
        ? optionsValue.filter((opt) => opt.name.toLowerCase().includes(searchValue))
        : [...optionsValue];
    });
  }

  updateCheckboxState(selectKey: string) {
    const selectedCount =
      this.filterForm.get(this.multiSelectConfig[selectKey].formControlName)
        .value?.length || 0;
    const totalCount = this.multiSelectConfig[selectKey].options().length;

    this.multiSelectConfig[selectKey].isAllSelected =
      selectedCount === totalCount;
    this.multiSelectConfig[selectKey].isIndeterminate =
      selectedCount > 0 && selectedCount < totalCount;
  }

  toggleAllSelection(selectKey: string, isAllSelected: boolean) {
    this.filterForm
      .get(this.multiSelectConfig[selectKey].formControlName)
      .setValue(
        isAllSelected
          ? this.multiSelectConfig[selectKey]
              .filteredData()
              .map((opt) => opt.id)
          : []
      );
    this.updateCheckboxState(selectKey);
  }

  searchKeyword(event: any) {}

  onClearFilter() {
    const companyId = this.filterForm.get("companyId").value;
    this.filterForm.reset();
    this.filterForm.get("uploadDate").setValue(new Date());
    //this.filterForm.get("companyId").setValue();
    if (companyId) {
      this.filterForm.get("companyId").setValue(companyId);
      this.fetchAllData();
    }
  }

  onFilter() {
    this.selectedDate = this.filterForm.get("uploadDate").value;
    this.fetchAllData();
  }

  onPageChange(event: any) {
    const selectedTab = this.groupTabConfig.tabs[this.selectedTabIndex()];
    selectedTab.pagination.update(() => {
      return {
        pageIndex: event.pageIndex,
        pageSize: event.pageSize,
        totalItems: event.length,
      };
    });
    this.getListData(this.selectedTabIndex());

  }

  private resetPagintaionDefaultValues() {
    this.groupTabConfig.tabs.forEach((tab) => {
      tab.pagination.update(() => {
        return {
          pageIndex: 0,
          pageSize: 50,
          totalItems: 100,
        };
      });

      tab.search.update(() => this.searchHandle || '');
    });
  }

  changeSort(e) {
    this.groupTabConfig.tabs[this.selectedTabIndex()].ordering.update(() => {
      return {
        active: e.active,
        direction: e.direction,
      };
    });
    //this.getListData(this.selectedTabIndex()); //se activa solo si es por un tab seleccionado
    this.fetchAllData()
  }

  private getListData(tabId: LoadComponentTabIds) {
    switch (tabId) {
      case LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE:
        this.getOrdersWithoutRoute();
        break;
      case LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_DATE:
        this.getOrdersWithoutDeliveryDate();
        break;
      case LoadComponentTabIds.ORDERS_WITHOUT_LOAD_DATE:
        // this.getOrdersWithoutLoadDate();
        break;
      case LoadComponentTabIds.ORDERS_WITHOUT_TRIP:
        this.getOrdersWithoutTrip();
        break;
      case LoadComponentTabIds.UNATTENDED_ORDERS:
          this.getUnattendedOrders();
          break;
      case LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP:
        this.getOrdersWithRouteAndTrip();
        break;
      default:
        break;
    }
  }


  //############################## Service Calls ##############################


  private fetchAllData(){
    this.resetPagintaionDefaultValues();
    // this.getOrdersWithoutRoute();
    // this.getOrdersWithoutLoadDate();
    //this.getOrdersWithoutDeliveryDate();
    this.getOrdersWithoutTrip();
    this.getUnattendedOrders();
    this.getOrdersWithRouteAndTrip();
  }

  private fetchCompanies(): void {
    this.companyService.findToComboBox().subscribe((res) => {
      this.companies.update(() => res.data);
      if (res.data.length === 1) {
        this.filterForm.get("companyId").setValue(res.data[0]._id);
        this.loadCalculationMode = res.data[0].loadCalculationMode;
        this.UMPActive = res.data[0].UMPActive
      }
    });
  }

  changeCompany(event){
    let findCompany = this.companies().find((company) => company._id === event);
    if(findCompany){
      this.loadCalculationMode = findCompany?.loadCalculationMode;
      this.UMPActive = findCompany?.UMPActive;
    }else{
      this.loadCalculationMode = undefined;
      this.UMPActive = false;
    }
  }

  private fetchSalesOfficesGroups(companyId: string): void {
    this.officegroupService.findByCompanyId(companyId).subscribe((res) => {
      this.salesOfficeGroup.update(() => res.data);
    });
  }

  private fetchSalesOffices(officeGroupId: string[]): void {
    this.salesOfficeService.findToComboBoxGroup(officeGroupId).subscribe((res) => {
      this.multiSelectConfig.salesOffice.options.update(() => res.data);
      this.multiSelectConfig.salesOffice.filteredData.set([...this.multiSelectConfig.salesOffice.options()]);
    });
  }

  private fetchAllRoute(companyId: string): void {
    this.routeService.findToComboBox(companyId).subscribe({
      next: (res) => {
        this.routes = res.data
        this.listFilterRoutes.update(() => this.routes.slice());
      },
      error: (err) => {
        this.snackBar.open(err.error.message || "Error fetching route","Cerrar",{ duration: 5000});
      },
    });
  }

  changueOptionsRoute(event){
    if (!this.routes) {
      return;
    }
    let search = event;
    if (!search) {
      this.listFilterRoutes.update(() => this.routes.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.listFilterRoutes.update(() => this.routes.filter(item => item.externalKeys?.code.toLowerCase().indexOf(search) > -1 || item.name.toLowerCase().indexOf(search) > -1))
  }

  private fetchAllAreaRoute(companyId: string): void {
    this.routeAreaService.findToComboBox(companyId).subscribe({
      next: (res) => {
        this.areaRoutes = res.data
        this.listFilterAreaRoutes.update(() => this.areaRoutes.slice());
      },
      error: (err) => {
        this.snackBar.open(err.error.message || "Error fetching area route","Cerrar",{ duration: 5000});
      },
    });
  }

  changueOptionsAreaRoute(event){
    if (!this.areaRoutes) {
      return;
    }
    let search = event;
    if (!search) {
      this.listFilterAreaRoutes.update(() => this.areaRoutes.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.listFilterAreaRoutes.update(() => this.areaRoutes.filter(item => item.name.toLowerCase().indexOf(search) > -1))
  }

  private fetchRouteGroups(comapnyId: string): void {
    this.routeGroupService.findByCompanyId(comapnyId).subscribe((res) => {
      this.routeGroup.update(() => res.data);
    });
  }

  private fetchSubRoutes(routeId: string[], routeArea: string[], routeGroupId: string[]): void {
    this.subrouteService.findToComboBoxByGroupAndModel(routeId, routeArea, routeGroupId, '08').subscribe((res) => {
      this.multiSelectConfig.subRoute.options.update(() => res.data);
      this.multiSelectConfig.subRoute.filteredData.set([...this.multiSelectConfig.subRoute.options()]);
    });
  }

  private getAllBusinessPartner(): void {
    const materialOptions = {
      search: this.searchBusiness ? this.searchBusiness: null,
      currentPage: 1,
      limitPerPage: 50,
      sortActive: "name",
      //sortDirection: "ASC"
    };

    this.businessPartnerService.findToComboBoxCustomer(
      materialOptions,
      [this.filterForm.value.companyId],
      this.filterForm.value.route ? [this.filterForm.value.route] : []
    ).subscribe({
      next: (res: IRequestResult<BusinessPartnerAttributes[]>) => {
        this.businessPartnerList = res?.data ?? [];
      },
      error: (err) => {
        this.snackBar.open(err.error.message || "Error al obtener socios de negocios.","Cerrar",{ duration: 5000});
      },
    })
  }

  changueOptionsBusiness(event){
    this.searchSubjectBusiness.next(event)
  }
  
  private setupBusinessSearchControl(): void {
    this.searchSubjectBusiness
    .pipe(
      debounceTime(500),
      distinctUntilChanged()
    )
    .subscribe((searchTerm) => {
      if(searchTerm){
        this.searchBusiness = searchTerm;
        this.getAllBusinessPartner();
      }
    });
  }

  //DEPRECATED
  private getUnattendedOrders(){
    // const selectedDate = new Date(this.filterForm.value.uploadDate);

    // let startDate = new Date();
    // startDate.setMonth(startDate.getMonth() - 1);

    // const uploadDate = new Date();

    // const { startUTC } = getStartAndEndOfDayUTC(startDate);
    // const { endUTC } = getStartAndEndOfDayUTC(uploadDate);
    // const date = {
    //   start: startUTC.toISOString(),
    //   end: endUTC.toISOString(),
    // };

     const selectedDate = new Date(this.filterForm.value.uploadDate);
    const endDate = new Date(selectedDate);
    
    const startDate = new Date(selectedDate);
    startDate.setMonth(startDate.getMonth() - 3);

    const { startUTC } = getStartAndEndOfDayUTC(startDate);
    const { endUTC } = getStartAndEndOfDayUTC(endDate); 
    const date = {
      start: startUTC.toISOString(),
      end: endUTC.toISOString(),
    };

    let companyId = this.filterForm.get("companyId").value;
    let numberCompanyId = this.companies().find((company) => company._id === companyId)?.id;

    this.LoadsService.findUnattendedOrders(
      "DT003",
      date,
      numberCompanyId,
      this.filterForm.get("salesOfficeGroupId").value,
      this.filterForm.get("salesOfficeId").value,
      this.filterForm.get("routeId").value,
      this.filterForm.get("routeAreaId").value,
      this.filterForm.get("routeGroupId").value,
      this.filterForm.get("subRouteId").value,
      this.filterForm.get("customerId").value,
      this.filterForm.get("documentClassId").value,
      {
        currentPage: this.groupTabConfig.tabs[LoadComponentTabIds.UNATTENDED_ORDERS].pagination().pageIndex + 1,
        limitPerPage: this.groupTabConfig.tabs[LoadComponentTabIds.UNATTENDED_ORDERS].pagination().pageSize,
        search: this.groupTabConfig.tabs[LoadComponentTabIds.UNATTENDED_ORDERS].search(),
        sort: this.groupTabConfig.tabs[LoadComponentTabIds.UNATTENDED_ORDERS].ordering().active as keyof OrderAttributes,
        sortDirection: this.groupTabConfig.tabs[LoadComponentTabIds.UNATTENDED_ORDERS].ordering().direction,
      }
    ).subscribe((res) => {
      this.groupTabConfig.tabs[LoadComponentTabIds.UNATTENDED_ORDERS].data.update(
        () => res.data[0].data
      );
      const total =
        res.data[0].pageInfo?.length > 0 ? res.data[0].pageInfo[0].total : 0;

      this.groupTabConfig.tabs[LoadComponentTabIds.UNATTENDED_ORDERS].count.update(() => total);
      this.groupTabConfig.tabs[LoadComponentTabIds.UNATTENDED_ORDERS].total.update(() => total);
    });
  }

  private getOrdersWithoutRoute() {
    let companyId = this.filterForm.get("companyId").value;
    let numberCompanyId = this.companies().find((company) => company._id === companyId)?.id;
    this.LoadsService.findOrdersWithoutRoute(
      "DT003",
      numberCompanyId,
      this.filterForm.get("salesOfficeGroupId").value,
      this.filterForm.get("salesOfficeId").value,
      this.filterForm.get("routeId").value,
      this.filterForm.get("routeAreaId").value,
      this.filterForm.get("routeGroupId").value,
      this.filterForm.get("subRouteId").value,
      this.filterForm.get("customerId").value,
      this.filterForm.get("documentClassId").value,
      {
        currentPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE].pagination().pageIndex + 1,
        limitPerPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE].pagination().pageSize,
        search: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE].search(),
        sort: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE].ordering().active as keyof OrderAttributes,
        sortDirection: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE].ordering().direction,
      }
    ).subscribe((res) => {
      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE].data.update(
        () => res.data[0].data
      );
      const total =
        res.data[0].pageInfo?.length > 0 ? res.data[0].pageInfo[0].total : 0;

      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE].count.update(() => total);
      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_ROUTE].total.update(() => total);
    });
  }

  // private getOrdersWithoutLoadDate() {
  //   let companyId = this.filterForm.get("companyId").value;
    
  //   if (!companyId) {
  //     return;
  //   }
    
  //   const date: Date = this.filterForm.value.uploadDate || new Date();
  //   const dateFormat = `${date.getFullYear()}-${this.addZero(date.getMonth() + 1)}-${this.addZero(date.getDate())}`;

  //   const filter = {
  //     companyId: companyId,
  //     uploadDate: dateFormat,
  //     salesOfficeGroupId: this.filterForm.get("salesOfficeGroupId").value ? [this.filterForm.get("salesOfficeGroupId").value] : [],
  //     salesOfficeId: this.filterForm.get("salesOfficeId").value || [],
  //     routeGroupId: this.filterForm.get("routeGroupId").value ? [this.filterForm.get("routeGroupId").value] : [],
  //     subrouteId: this.filterForm.get("subRouteId").value || [],
  //     materialGroupId: [],
  //     materialId: [],
  //     routeId: this.filterForm.get("routeId").value ? [this.filterForm.get("routeId").value] : [],
  //     routeAreaId: this.filterForm.get("routeAreaId").value ? [this.filterForm.get("routeAreaId").value] : [],
  //     customerId: this.filterForm.get("customerId").value ? [this.filterForm.get("customerId").value] : [],
  //     documentClassId: this.filterForm.get("documentClassId").value || [],
  //   };

    
  //   this.salesDistributionService.findOrderNotLoad(filter, {
  //     id: "",
  //     indexPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_LOAD_DATE].pagination().pageIndex + 1,
  //     limit: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_LOAD_DATE].pagination().pageSize,
  //     sortBy: "name",
  //     sort: "ASC",
  //   }).subscribe((res: any) => {
  //     this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_LOAD_DATE].data.update(
  //       () => res.data || []
  //     );
  //     const total = res.total || 0;

  //     this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_LOAD_DATE].count.update(() => total);
  //     this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_LOAD_DATE].total.update(() => total);
  //   });
  // }

  private addZero(value: number): string {
    return value < 10 ? `0${value}` : value.toString();
  }

  //DEPRECATED
  private getOrdersWithoutDeliveryDate() {
    let companyId = this.filterForm.get("companyId").value;
    let numberCompanyId = this.companies().find((company) => company._id === companyId)?.id;
    this.LoadsService.findOrdersWithoutDeliveryDate(
      "DT003",
      numberCompanyId,
      this.filterForm.get("salesOfficeGroupId").value,
      this.filterForm.get("salesOfficeId").value,
      this.filterForm.get("routeId").value,
      this.filterForm.get("routeAreaId").value,
      this.filterForm.get("routeGroupId").value,
      this.filterForm.get("subRouteId").value,
      this.filterForm.get("customerId").value,
      this.filterForm.get("documentClassId").value,
      {
        currentPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_DATE].pagination().pageIndex + 1,
        limitPerPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_DATE].pagination().pageSize,
        search: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_DATE].search(),
        sort: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_DATE].ordering().active as keyof OrderAttributes,
        sortDirection: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_DATE].ordering().direction,
      }
    ).subscribe((res) => {
      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_DATE].data.update(
        () => res.data[0].data
      );
      const total =
        res.data[0].pageInfo?.length > 0 ? res.data[0].pageInfo[0].total : 0;

      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_DATE].count.update(() => total);
      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_DELIVERY_DATE].total.update(() => total);
    });
  }


  private getOrdersWithoutTrip() {
    const { startUTC, endUTC } = getStartAndEndOfDayUTC(this.filterForm.value.uploadDate);
    const date = {
      start: startUTC.toISOString(),
      end: endUTC.toISOString(),
    };

    let companyId = this.filterForm.get("companyId").value;
    let numberCompanyId = this.companies().find((company) => company._id === companyId)?.id;
    this.LoadsService.findOrdersWithoutTrip(
      "DT003",
      date,
      numberCompanyId,
      this.filterForm.get("salesOfficeGroupId").value,
      this.filterForm.get("salesOfficeId").value,
      this.filterForm.get("routeId").value,
      this.filterForm.get("routeAreaId").value,
      this.filterForm.get("routeGroupId").value,
      this.filterForm.get("subRouteId").value,
      this.filterForm.get("customerId").value,
      this.filterForm.get("documentClassId").value,
      {
        currentPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_TRIP].pagination().pageIndex + 1,
        limitPerPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_TRIP].pagination().pageSize,
        search: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_TRIP].search(),
        sort: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_TRIP].ordering().active as keyof OrderAttributes,
        sortDirection: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_TRIP].ordering().direction,
      }
    ).subscribe((res) => {
      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_TRIP].data.update(
        () => res.data[0].data
      );
      const total =
        res.data[0].pageInfo?.length > 0 ? res.data[0].pageInfo[0].total : 0;

      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_TRIP].count.update(() => total);
      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITHOUT_TRIP].total.update(() => total);
    });
  }


  private getOrdersWithRouteAndTrip() {
    const { startUTC, endUTC } = getStartAndEndOfDayUTC(this.filterForm.value.uploadDate);
    const date = {
      start: startUTC.toISOString(),
      end: endUTC.toISOString(),
    };

    let companyId = this.filterForm.get("companyId").value;
    this.LoadsService.getTripsByLoadDocs(
      "DT003",
      date,
      // NOTE: THIS IS COMPANY MONGO ID FROM FILTER
      this.filterForm.getRawValue().companyId,
      this.filterForm.get("salesOfficeGroupId").value,
      this.filterForm.get("salesOfficeId").value,
      this.filterForm.get("routeId").value,
      this.filterForm.get("routeAreaId").value,
      this.filterForm.get("routeGroupId").value,
      this.filterForm.get("subRouteId").value,
      this.filterForm.get("customerId").value,
      this.filterForm.get("documentClassId").value,
      {
        currentPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].pagination().pageIndex + 1,
        limitPerPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].pagination().pageSize,
        search: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].search(),
        sort: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].ordering().active as keyof OrderAttributes,
        sortDirection: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].ordering().direction,
      }
    ).subscribe((res) => {
      console.log({res});
      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].data.update(
        () => res.data.data
      );
      const total =
        res?.data?.pageInfo?.[0]?.total ?? 0;
      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].count.update(() =>
        res?.data?.pageInfo?.[0]?.totalOrders ?? 0
      );
      this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].total.update(() => total);
    });
  }

  getOrdersFuture() {
    const currentDate = this.filterForm.value.uploadDate?.toISOString?.() ?? String(this.filterForm.value.uploadDate);
    if (currentDate === this.lastLoadedFutureDate && this.selectedOrdersFuture().days.length > 0) return;
    if (this.isLoadingOrdersFuture()) return;
    this.isLoadingOrdersFuture.set(true);
    const selectedDate = new Date(this.filterForm.value.uploadDate);

    const startDate = new Date(selectedDate);
    startDate.setDate(startDate.getDate() + 1);

    const endDate = new Date(selectedDate);
    endDate.setMonth(endDate.getMonth() + 3);

    const { startUTC } = getStartAndEndOfDayUTC(startDate); // Inicio del día de `selectedDate`
    const { endUTC } = getStartAndEndOfDayUTC(endDate); // Fin del día de `endDate`
    const date = {
      start: startUTC.toISOString(),
      end: endUTC.toISOString(),
    };

    let companyId = this.filterForm.get("companyId").value;
    let numberCompanyId = this.companies().find((company) => company._id === companyId)?.id;
    this.LoadsService.findOrdersFuture(
      "DT003",
      date,
      numberCompanyId,
      this.filterForm.get("salesOfficeGroupId").value,
      this.filterForm.get("salesOfficeId").value,
      this.filterForm.get("routeId").value,
      this.filterForm.get("routeAreaId").value,
      this.filterForm.get("routeGroupId").value,
      this.filterForm.get("subRouteId").value,
      this.filterForm.get("customerId").value,
      this.filterForm.get("documentClassId").value,
      {
        currentPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].pagination().pageIndex + 1,
        limitPerPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].pagination().pageSize,
        search: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].search(),
        sort: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].ordering().active as keyof OrderAttributes,
        sortDirection: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].ordering().direction,
      }
    ).subscribe({
      next: (res) => {
        if(res.data){
          this.selectedOrdersFuture.set({
            days: res.data?.days,
            total: res.data?.total
          });
          this.lastLoadedFutureDate = currentDate;
        }
        this.isLoadingOrdersFuture.set(false);
      },
      error: () => {
        this.isLoadingOrdersFuture.set(false);
      }
    });
  }

  getOrdersPast() {
    const currentDate = this.filterForm.value.uploadDate?.toISOString?.() ?? String(this.filterForm.value.uploadDate);
    if (currentDate === this.lastLoadedPastDate && this.selectedOrdersPast().days.length > 0) return;
    if (this.isLoadingOrdersPast()) return;
    this.isLoadingOrdersPast.set(true);
    const selectedDate = new Date(this.filterForm.value.uploadDate);

    const endDate = new Date(selectedDate);
    endDate.setDate(endDate.getDate() - 1);

    const startDate = new Date(selectedDate);
    startDate.setMonth(startDate.getMonth() - 3);

    const { startUTC } = getStartAndEndOfDayUTC(startDate);
    const { endUTC } = getStartAndEndOfDayUTC(endDate);
    const date = {
      start: startUTC.toISOString(),
      end: endUTC.toISOString(),
    };

    let companyId = this.filterForm.get("companyId").value;
    let numberCompanyId = this.companies().find((company) => company._id === companyId)?.id;
    this.LoadsService.findOrdersFuture(
      "DT003",
      date,
      numberCompanyId,
      this.filterForm.get("salesOfficeGroupId").value,
      this.filterForm.get("salesOfficeId").value,
      this.filterForm.get("routeId").value,
      this.filterForm.get("routeAreaId").value,
      this.filterForm.get("routeGroupId").value,
      this.filterForm.get("subRouteId").value,
      this.filterForm.get("customerId").value,
      this.filterForm.get("documentClassId").value,
      {
        currentPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].pagination().pageIndex + 1,
        limitPerPage: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].pagination().pageSize,
        search: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].search(),
        sort: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].ordering().active as keyof OrderAttributes,
        sortDirection: this.groupTabConfig.tabs[LoadComponentTabIds.ORDERS_WITH_ROUTE_AND_TRIP].ordering().direction,
      }
    ).subscribe({
      next: (res) => {
        if(res.data){
          this.selectedOrdersPast.set({
            days: res.data?.days,
            total: res.data?.total
          });
          this.lastLoadedPastDate = currentDate;
        }
        this.isLoadingOrdersPast.set(false);
      },
      error: () => {
        this.isLoadingOrdersPast.set(false);
      }
    });
  }
  //############################## Service Calls ##############################

  subscribeToActivatedRoute() {
    this.activatedRoute.queryParams
    .pipe(
      takeUntil(this._onDestroy)
    )
    .subscribe(params => {

      const forceInitialTabId = params?.[LOAD_COMPONENT_QUERY_PARAMS.forceInitialTabId.queryParam];

      if (forceInitialTabId && typeof Number(forceInitialTabId) === "number" && !this.onInitFinished) {

       this.setInitialTabByTabId(Number(forceInitialTabId));

      }
    });
  }

  setInitialTabByTabId(tabId: number) {
    const tabFoundIndex = this.groupTabConfig.tabs.findIndex(tab => tab.id === tabId);
    if (tabFoundIndex !== -1) {
      this.selectedTabIndex.set(tabFoundIndex);
    }
  }

  //#region Map
  openModal(element): void {
    let startUTC: Date;
    let endUTC: Date;

    if(element.id === LoadComponentTabIds.UNATTENDED_ORDERS){
      const selectedDate = new Date(this.filterForm.value.uploadDate);
      const endDate = new Date(selectedDate);
      const startDate = new Date(selectedDate);

      startDate.setMonth(startDate.getMonth() - 3);

      ({ startUTC } = getStartAndEndOfDayUTC(startDate));
      ({ endUTC } = getStartAndEndOfDayUTC(endDate)); 
    }

    if(element.id === LoadComponentTabIds.ORDERS_WITHOUT_TRIP){
      ({ startUTC, endUTC } = getStartAndEndOfDayUTC(this.filterForm.value.uploadDate));
    }

    const dateFilter = {
      start: startUTC.toISOString(),
      end: endUTC.toISOString(),
    };

    const companyIdForm = this.filterForm.get("companyId").value;
    const numberCompanyId = this.companies().find(c => c._id === companyIdForm)?.id;

    const data: MapModalInputData = {
      type: MapModalTypeEnum.GROUPED_WITH_FILTER,
      useDate: false,
      tripId: null,
      element: null,
      document: MonitorDocumentTypeEnum.DOCUMENT,
      groupDocuments: [],
      map: {
        displayMode: 'drawer',
        captions: { title: 'Ubicación' }
      },
      filters: {
        mode: element.id === LoadComponentTabIds.ORDERS_WITHOUT_TRIP ? 
          MapFilterTypeEnum.EXTRA_WITHOUT_TRIP : MapFilterTypeEnum.EXTRA_UNATTENDED_ORDERS,
        filters: {
          statusIds: [StatusEnum.ACTIVE],
          startDate: dateFilter.start,
          endDate: dateFilter.end,
          companyId: numberCompanyId,
          ...(this.filterForm.get("salesOfficeGroupId")?.value ? { salesOfficeGroupId: this.filterForm.get("salesOfficeGroupId").value } : {}),
          ...(this.filterForm.get("salesOfficeId")?.value ? { salesOffice: [this.filterForm.get("salesOfficeId").value] } : {}),
          ...(this.filterForm.get("routeId")?.value ? { route: this.filterForm.get("routeId").value } : {}),
          ...(this.filterForm.get("routeAreaId")?.value ? { routeAreaId: this.filterForm.get("routeAreaId").value } : {}),
          ...(this.filterForm.get("routeGroupId")?.value ? { routeGroupId: this.filterForm.get("routeGroupId").value } : {}),
          ...(this.filterForm.get("subRouteId")?.value ? { subRouteId: this.filterForm.get("subRouteId").value } : {}),
          ...(this.filterForm.get("customerId")?.value ? { customerId: [this.filterForm.get("customerId").value] } : {}),
          ...(this.filterForm.get("documentClassId")?.value ? { documentClassId: this.filterForm.get("documentClassId").value } : {}),
        }
      }
    }

    this.openMap(data)
  }
  
  openMap(data) {
    this.dialog.open(MapModalComponent, {
      width: '90%',
      //height: '80%',
      data: data
    });
  }
  //#endregion
}
